import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { locationsAPI, roomsAPI, employeesAPI } from '../lib/api';
import { useOrgLabels } from '../contexts/OrgLabelsContext';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus, DoorOpen, Users, MoreHorizontal, Pencil, Trash2,
  MapPin, Search, ArrowUp, ArrowDown, ArrowUpDown, FileText,
  Download, Upload, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { RoomFormDialog, EQUIPMENT_OPTIONS } from './rooms/RoomFormDialog';
import { KostentraegerExportDialog } from './rooms/KostentraegerExportDialog';
import { openAuthFile } from '../lib/openAuthFile';
import { BulkImportDialog } from '../components/ui/bulk-import-dialog';
import { ConfirmDeleteDialog } from '../components/ui/confirm-delete-dialog';

export default function Rooms() {
  const { isAdmin, hasPermission } = useAuth();
  const canManageRooms = isAdmin || hasPermission('rooms', 'edit');
  const [rooms, setRooms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocationId, setSelectedLocationId] = useState('all');
  const orgL = useOrgLabels();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [kostentraegerOpen, setKostentraegerOpen] = useState(false);

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const params = { format };
      if (selectedLocationId !== 'all') params.location_id = selectedLocationId;
      const res = await roomsAPI.exportRooms(params);
      const ext = format === 'xlsx' ? 'xlsx' : 'csv';
      const mime = format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv;charset=utf-8';
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `raeume_export.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Export gestartet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  // Room Dialog
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [savingRoom, setSavingRoom] = useState(false);
  const [roomForm, setRoomForm] = useState({
    name: '',
    description: '',
    location_id: '',
    capacity: 1,
    equipment: [],
    color: '#3B82F6',
    is_active: true,
    bookable_by_employees: false,
    room_type: '',
    floor: '',
    building: '',
    size_sqm: '',
    ownership_type: '',
    ownership_document_url: '',
    ownership_document_filename: '',
    hourly_rate: '',
    min_booking_hours: 0.5,
    max_booking_hours: 8,
    requires_approval: false,
    cleaning_price_per_day: '',
    cleaning_enabled: true,
    custom_fields: [],
  });
  const [uploadingOwnership, setUploadingOwnership] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [roomsRes, locRes] = await Promise.all([
        api.get('/rooms'),
        locationsAPI.getAll(),
      ]);

      setRooms(roomsRes.data);

      // Alle Standorte für das Raum-Formular (Standort-Auswahl beim Anlegen/Bearbeiten).
      setLocations(locRes.data);

      // Für den Standard-Filter: nur Standorte, die bereits Räume haben.
      const roomLocationIds = new Set(roomsRes.data.map(r => r.location_id).filter(Boolean));
      const filteredLocations = locRes.data.filter(loc => roomLocationIds.has(loc.id));

      // Standard-Filter: Hauptstandort des angemeldeten Nutzers (falls dort Räume
      // existieren), sonst „Alle Standorte". Nur beim ersten Laden setzen.
      try {
        const meRes = await employeesAPI.getMyProfile();
        const myLocationId = meRes.data?.location_id || null;
        if (myLocationId && filteredLocations.some(l => l.id === myLocationId)) {
          setSelectedLocationId(myLocationId);
        }
      } catch {
        /* Kein Mitarbeiterprofil (z.B. reiner Admin) → bei „Alle" bleiben. */
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Room CRUD
  const openRoomDialog = (room = null) => {
    if (room) {
      setEditingRoom(room);
      setRoomForm({
        name: room.name,
        description: room.description || '',
        location_id: room.location_id || '',
        capacity: room.capacity,
        equipment: room.equipment || [],
        color: room.color || '#3B82F6',
        is_active: room.is_active,
        bookable_by_employees: room.bookable_by_employees || false,
        room_type: room.room_type || '',
        floor: room.floor || '',
        building: room.building || '',
        size_sqm: room.size_sqm || '',
        ownership_type: room.ownership_type || '',
        ownership_document_url: room.ownership_document_url || '',
        ownership_document_filename: room.ownership_document_filename || '',
        hourly_rate: room.hourly_rate || '',
        min_booking_hours: room.min_booking_hours || 0.5,
        max_booking_hours: room.max_booking_hours || 8,
        requires_approval: room.requires_approval || false,
        cleaning_price_per_day: room.cleaning_price_per_day ?? '',
        cleaning_enabled: room.cleaning_enabled !== false,
        custom_fields: room.custom_fields || [],
      });
    } else {
      setEditingRoom(null);
      setRoomForm({
        name: '',
        description: '',
        location_id: selectedLocationId !== 'all' ? selectedLocationId : '',
        capacity: 1,
        equipment: [],
        color: '#3B82F6',
        is_active: true,
        bookable_by_employees: false,
        room_type: '',
        floor: '',
        building: '',
        size_sqm: '',
        ownership_type: '',
        ownership_document_url: '',
        ownership_document_filename: '',
        hourly_rate: '',
        min_booking_hours: 0.5,
        max_booking_hours: 8,
        requires_approval: false,
        cleaning_price_per_day: '',
        cleaning_enabled: true,
        custom_fields: [],
      });
    }
    setRoomDialogOpen(true);
  };

  const handleOwnershipFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 10 MB)');
      return;
    }
    setUploadingOwnership(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/certificate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRoomForm((prev) => ({
        ...prev,
        ownership_document_url: res.data.url,
        ownership_document_filename: res.data.original_filename || file.name,
      }));
      toast.success('Nachweis hochgeladen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploadingOwnership(false);
    }
  };

  const handleSaveRoom = async () => {
    if (!roomForm.name) {
      toast.error('Bitte Raumnamen eingeben');
      return;
    }

    setSavingRoom(true);
    try {
      const payload = {
        ...roomForm,
        location_id: roomForm.location_id || null,
        room_type: roomForm.room_type || null,
        floor: roomForm.floor || null,
        building: roomForm.building || null,
        size_sqm: roomForm.size_sqm ? parseFloat(roomForm.size_sqm) : null,
        ownership_type: roomForm.ownership_type || null,
        ownership_document_url: roomForm.ownership_document_url || null,
        ownership_document_filename: roomForm.ownership_document_filename || null,
        hourly_rate: roomForm.hourly_rate ? parseFloat(roomForm.hourly_rate) : null,
        min_booking_hours: parseFloat(roomForm.min_booking_hours) || 0.5,
        max_booking_hours: parseFloat(roomForm.max_booking_hours) || 8,
        cleaning_price_per_day: roomForm.cleaning_price_per_day !== '' && roomForm.cleaning_price_per_day != null
          ? parseFloat(roomForm.cleaning_price_per_day) : null,
        cleaning_enabled: roomForm.cleaning_enabled !== false,
      };

      if (editingRoom) {
        await api.put(`/rooms/${editingRoom.id}`, payload);
        toast.success('Raum aktualisiert');
      } else {
        await api.post('/rooms', payload);
        toast.success('Raum erstellt');
      }
      setRoomDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingRoom(false);
    }
  };

  const handleDeleteRoom = async (id) => {
    try {
      await api.delete(`/rooms/${id}`);
      toast.success('Raum gelöscht');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Confirm-Dialog State (für Lösch-Bestätigung im Dropdown-Menü)
  const [roomToDelete, setRoomToDelete] = useState(null);

  // Filter rooms by selected location and search term
  const searchLower = search.trim().toLowerCase();
  const roomsFilteredByLocation = rooms
    .filter((r) => selectedLocationId === 'all' || r.location_id === selectedLocationId)
    .filter((r) =>
      !searchLower ||
      r.name.toLowerCase().includes(searchLower) ||
      (r.description || '').toLowerCase().includes(searchLower) ||
      (r.location_name || '').toLowerCase().includes(searchLower) ||
      (r.room_type || '').toLowerCase().includes(searchLower) ||
      (r.building || '').toLowerCase().includes(searchLower)
    )
    .slice()
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const valA = a[sortBy];
      const valB = b[sortBy];
      // null / undefined / leere Strings ans Ende
      const emptyA = valA === undefined || valA === null || valA === '';
      const emptyB = valB === undefined || valB === null || valB === '';
      if (emptyA && emptyB) return 0;
      if (emptyA) return 1;
      if (emptyB) return -1;
      if (typeof valA === 'number' && typeof valB === 'number') {
        return (valA - valB) * dir;
      }
      if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        return (Number(valA) - Number(valB)) * dir;
      }
      return String(valA).localeCompare(String(valB), 'de', { sensitivity: 'base' }) * dir;
    });

  // Count rooms per location for badges
  const roomsPerLocation = locations.reduce((acc, loc) => {
    acc[loc.id] = rooms.filter(r => r.location_id === loc.id && r.is_active).length;
    return acc;
  }, {});

  // Filterleiste: nur Standorte anzeigen, die tatsächlich Räume haben.
  const filterLocations = locations.filter(loc => rooms.some(r => r.location_id === loc.id));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={`skel-${i}`} className="h-40 rounded-xl" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="rooms-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-xl sm:text-3xl text-foreground">Raumverwaltung</h1>
            <p className="text-muted-foreground mt-1">
              {roomsFilteredByLocation.filter(r => r.is_active).length} aktive Räume
              {selectedLocationId !== 'all' && ` am ${locations.find(l => l.id === selectedLocationId)?.name}`}
            </p>
          </div>
          {canManageRooms && (
            <div className="flex flex-wrap items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-full"
                    disabled={exporting}
                    data-testid="rooms-export-trigger"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Exportieren
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="rooms-export-csv">
                    CSV (.csv)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="rooms-export-xlsx">
                    Excel (.xlsx)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setKostentraegerOpen(true)}
                data-testid="rooms-export-kostentraeger"
              >
                <FileText className="mr-2 h-4 w-4 text-primary" />
                Kostenträger-Meldung exportieren
              </Button>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setImportOpen(true)}
                data-testid="rooms-import-btn"
              >
                <Upload className="mr-2 h-4 w-4" />
                Importieren
              </Button>
              <Button className="rounded-full" onClick={() => openRoomDialog()} data-testid="new-room-btn">
                <Plus className="mr-2 h-4 w-4" />
                Neuer Raum
              </Button>
            </div>
          )}
        </div>

        {/* Location Filter */}
        {filterLocations.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 p-4 bg-muted/30 rounded-lg border">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{orgL.location}:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedLocationId === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedLocationId('all')}
                data-testid="location-filter-all"
              >
                Alle {orgL.locations}
                <Badge variant="secondary" className="ml-2 text-xs">
                  {rooms.filter(r => r.is_active).length}
                </Badge>
              </Button>
              {filterLocations.map(loc => (
                <Button
                  key={loc.id}
                  variant={selectedLocationId === loc.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedLocationId(loc.id)}
                  data-testid={`location-filter-${loc.id}`}
                >
                  {loc.name}
                  {roomsPerLocation[loc.id] > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {roomsPerLocation[loc.id]}
                    </Badge>
                  )}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Suche */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            placeholder={`Suche nach Name, ${orgL.location}, Typ ...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="rooms-search"
          />
        </div>

        {/* Tabellarische Ansicht (Desktop) */}
        {roomsFilteredByLocation.length > 0 ? (
          <>
            <div className="hidden md:block rounded-lg border bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[6px] p-0" />
                    <TableHead>
                      <SortHeader field="name" label="Name / Nummer" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortHeader field="building" label="Etage / Gebäude" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortHeader field="room_type" label="Typ" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-center">
                      <SortHeader field="capacity" label="Plätze" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="justify-center" />
                    </TableHead>
                    <TableHead>Ausstattung</TableHead>
                    <TableHead className="text-center">
                      <SortHeader field="size_sqm" label="Größe (m²)" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="justify-center" />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortHeader field="cleaning_price_per_day" label="Reinigung/Tag" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="justify-end" />
                    </TableHead>
                    <TableHead>
                      <SortHeader field="ownership_type" label="Nachweis" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-center">
                      <SortHeader field="bookable_by_employees" label="Buchbar" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="justify-center" />
                    </TableHead>
                    <TableHead>
                      <SortHeader field="location_name" label={orgL.location} sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    {canManageRooms && <TableHead className="w-[60px] text-right">Aktionen</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roomsFilteredByLocation.map((room) => (
                    <TableRow
                      key={room.id}
                      className={!room.is_active ? 'opacity-60' : ''}
                      data-testid={`room-row-${room.id}`}
                    >
                      <TableCell className="p-0">
                        <div className="w-[6px] h-12" style={{ backgroundColor: room.color || '#3B82F6' }} />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{room.name}</div>
                        {room.description && (
                          <div className="text-xs text-muted-foreground line-clamp-1 max-w-xs">{room.description}</div>
                        )}
                        {!room.is_active && (
                          <Badge variant="secondary" className="text-[10px] mt-1">Inaktiv</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(() => {
                          const f = room.floor;
                          const floorStr = f !== '' && f !== null && f !== undefined ? String(f) : null;
                          const parts = [floorStr, room.building].filter(Boolean);
                          return parts.length ? parts.join(' · ') : <span className="text-muted-foreground">—</span>;
                        })()}
                      </TableCell>
                      <TableCell>
                        {room.room_type ? (
                          <Badge variant="outline" className="text-xs">{room.room_type}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        <span className="inline-flex items-center gap-1">
                          <Users className="h-3 w-3 text-muted-foreground" />
                          {room.capacity}
                        </span>
                      </TableCell>
                      <TableCell>
                        {room.equipment && room.equipment.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[220px]">
                            {room.equipment.slice(0, 3).map((eq) => (
                              <span key={eq} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                                {EQUIPMENT_OPTIONS.find((e) => e.value === eq)?.label || eq}
                              </span>
                            ))}
                            {room.equipment.length > 3 && (
                              <span className="text-[10px] text-muted-foreground">+{room.equipment.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {room.size_sqm ? (
                          <span>{room.size_sqm} m²</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm" data-testid={`room-cleaning-price-${room.id}`}>
                        {room.cleaning_price_per_day != null && room.cleaning_price_per_day !== '' ? (
                          <span>{Number(room.cleaning_price_per_day).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {room.ownership_type ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                room.ownership_type === 'owned'
                                  ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400'
                                  : 'border-amber-500/40 text-amber-700 dark:text-amber-400'
                              }`}
                            >
                              {room.ownership_type === 'owned' ? 'Eigentum' : 'Mietobjekt'}
                            </Badge>
                            {room.ownership_document_url && (
                              <button
                                type="button"
                                onClick={() =>
                                  openAuthFile(room.ownership_document_url, { filename: room.ownership_document_filename })
                                    .catch((err) => toast.error(`Anzeigen fehlgeschlagen: ${err.message || err}`))
                                }
                                className="text-primary hover:underline text-xs inline-flex items-center gap-0.5"
                                data-testid={`view-ownership-${room.id}`}
                                title="Nachweis öffnen"
                              >
                                <FileText className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {room.bookable_by_employees ? (
                          <Badge variant="secondary" className="text-xs">Ja</Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">Nein</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {room.location_name ? (
                          <span className="inline-flex items-center gap-1 text-sm">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            {room.location_name}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      {canManageRooms && (
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`room-menu-${room.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openRoomDialog(room)} data-testid={`edit-room-${room.id}`}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Bearbeiten
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setRoomToDelete(room)} className="text-destructive" data-testid={`delete-room-${room.id}`}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                Löschen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile-Karten (statt horizontaler Scroll-Tabelle) */}
            <div className="md:hidden space-y-3">
              {roomsFilteredByLocation.map((room) => (
                <Card
                  key={room.id}
                  className={`border-l-4 ${!room.is_active ? 'opacity-60' : ''}`}
                  style={{ borderLeftColor: room.color || '#3B82F6' }}
                  data-testid={`room-card-mobile-${room.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold truncate">{room.name}</h3>
                          {!room.is_active && <Badge variant="secondary" className="text-[10px]">Inaktiv</Badge>}
                          {room.bookable_by_employees && <Badge variant="secondary" className="text-[10px]">Buchbar</Badge>}
                        </div>
                        {room.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{room.description}</p>
                        )}
                      </div>
                      {canManageRooms && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 -mr-1">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openRoomDialog(room)}>
                              <Pencil className="mr-2 h-4 w-4" />Bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setRoomToDelete(room)} className="text-destructive">
                              <Trash2 className="mr-2 h-4 w-4" />Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                      {room.location_name && (
                        <><dt className="text-muted-foreground">{orgL.location}</dt><dd className="truncate">{room.location_name}</dd></>
                      )}
                      {room.room_type && (
                        <><dt className="text-muted-foreground">Typ</dt><dd>{room.room_type}</dd></>
                      )}
                      <dt className="text-muted-foreground">Plätze</dt><dd>{room.capacity}</dd>
                      {room.size_sqm && (
                        <><dt className="text-muted-foreground">Größe</dt><dd>{room.size_sqm} m²</dd></>
                      )}
                      {(room.floor !== '' && room.floor !== null && room.floor !== undefined) || room.building ? (
                        <>
                          <dt className="text-muted-foreground">Etage / Gebäude</dt>
                          <dd>
                            {(() => {
                              const f = room.floor;
                              const floorStr = f !== '' && f !== null && f !== undefined ? String(f) : null;
                              return [floorStr, room.building].filter(Boolean).join(' · ');
                            })()}
                          </dd>
                        </>
                      ) : null}
                      {room.ownership_type && (
                        <>
                          <dt className="text-muted-foreground">Nachweis</dt>
                          <dd className="flex items-center gap-1.5">
                            <span>{room.ownership_type === 'owned' ? 'Eigentum' : 'Mietobjekt'}</span>
                            {room.ownership_document_url && (
                              <button
                                type="button"
                                onClick={() =>
                                  openAuthFile(room.ownership_document_url, { filename: room.ownership_document_filename })
                                    .catch((err) => toast.error(`Anzeigen fehlgeschlagen: ${err.message || err}`))
                                }
                                className="text-primary hover:underline inline-flex items-center"
                                title="Nachweis öffnen"
                              >
                                <FileText className="h-3 w-3" />
                              </button>
                            )}
                          </dd>
                        </>
                      )}
                      {room.hourly_rate && (
                        <><dt className="text-muted-foreground">Stundensatz</dt><dd>{room.hourly_rate} €/h</dd></>
                      )}
                    </dl>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <Card className="border-border/60">
            <CardContent className="py-12">
              <div className="text-center">
                <DoorOpen className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <h3 className="font-heading font-semibold text-lg">Keine Räume</h3>
                <p className="text-muted-foreground mt-1">
                  {selectedLocationId !== 'all'
                    ? `An diesem ${orgL.location} gibt es keine Räume.`
                    : 'Noch keine Räume angelegt.'}
                </p>
                {canManageRooms && (
                  <Button className="mt-4 rounded-full" onClick={() => openRoomDialog()}>
                    <Plus className="mr-2 h-4 w-4" />
                    Ersten Raum anlegen
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <RoomFormDialog
          open={roomDialogOpen} onOpenChange={setRoomDialogOpen}
          editingRoom={editingRoom} roomForm={roomForm} setRoomForm={setRoomForm}
          onSave={handleSaveRoom} saving={savingRoom} locations={locations}
          onOwnershipUpload={handleOwnershipFileUpload}
          uploadingOwnership={uploadingOwnership}
        />

        <BulkImportDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          title="Räume importieren"
          downloadTemplate={() => roomsAPI.getImportTemplate({ format: 'xlsx' })}
          runImport={(file, dryRun) => roomsAPI.importRooms(file, dryRun)}
          templateFilename="raeume_vorlage.xlsx"
          onSuccess={loadData}
        />

        <ConfirmDeleteDialog
          open={!!roomToDelete}
          onOpenChange={(o) => !o && setRoomToDelete(null)}
          title="Raum löschen?"
          description={
            roomToDelete
              ? `Möchten Sie den Raum „${roomToDelete.name}“ wirklich löschen? Diese Aktion wird in den Zugriffsprotokollen vermerkt.`
              : ''
          }
          onConfirm={() => {
            if (roomToDelete) handleDeleteRoom(roomToDelete.id);
            setRoomToDelete(null);
          }}
          testIdPrefix="confirm-delete-room"
        />

        <KostentraegerExportDialog
          open={kostentraegerOpen}
          onOpenChange={setKostentraegerOpen}
          rooms={roomsFilteredByLocation}
        />
      </div>
    </DashboardLayout>
  );
}

const SortHeader = ({ field, label, sortBy, sortDir, onSort, className = '' }) => {
  const active = sortBy === field;
  const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1 text-xs font-medium hover:text-foreground transition-colors ${active ? 'text-foreground' : 'text-muted-foreground'} ${className}`}
      data-testid={`sort-${field}`}
    >
      {label}
      <Icon className="h-3 w-3" />
    </button>
  );
};
